import { css } from 'styled-components/macro'

/**
 * Canonical single-line form-control height (FhField text / number / adorned).
 * Combobox + DatePicker shells lock to this so row-aligned controls match.
 * Prefer the CSS var `--control-height` in modules; use this constant in JS.
 */
export const CONTROL_HEIGHT = '3.25rem'

interface BorderColorProps {
  error?: boolean
  valid?: boolean
}

/**
 * Resolves the input border color to a Flowhub Design System SEMANTIC token so
 * the ring re-binds with the theme (pearl-identical today):
 *  - error  → --status-danger (== --alert #d04c00 in pearl; the same semantic
 *             pair the InputErrorMessage text uses, so ring + message move
 *             together, and dark themes lift the danger hue instead of stranding
 *             a fixed-orange ring on a themed field)
 *  - valid  → --focus-ring (deep-swell)
 *  - default→ --border-default (gray-300 hairline)
 */
export const baseBorderColor = (props: BorderColorProps): string => {
  const { error, valid } = props

  return error
    ? 'var(--status-danger)'
    : valid
    ? 'var(--focus-ring)'
    : 'var(--border-default)'
}

export const inputBaseStyle = css`
  background-color: var(--surface-card);
  border: 1px solid ${baseBorderColor};
  border-radius: var(--radius-control);
  color: var(--text-strong);
  font-family: inherit;
  font-weight: var(--font-weight-regular);
  font-size: var(--mantine-font-size-body-lg);
  outline: none;
  padding: 1rem;
  transition: border 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    background-color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

  ::placeholder {
    color: var(--gray-400);
    font-style: italic;
    user-select: none;
  }

  &[type='number'] {
    font-variant-numeric: tabular-nums;
  }

  :disabled {
    background-color: var(--gray-100);
    border-color: var(--border-subtle);
    color: var(--gray-400);
    user-select: none;

    ::placeholder {
      color: currentcolor;
    }
  }

  :focus {
    border-color: var(--focus-ring);
  }
`
